import { defineComponent as _defineComponent } from 'vue'
import { vShow as _vShow, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = ["src", "alt", "loading", "decoding"]

import { computed, onBeforeUnmount, onMounted, ref, useSlots, watch } from 'vue'
import placeholderImage from '@web/ui-kit/images/placeholder.jpg'

type ImageMode = 'img' | 'background'
type ImageState = 'loading' | 'loaded' | 'error'
type ObjectFit = 'cover' | 'contain' | 'fill' | 'none'
type ShimmerVariant = 'sweep' | 'pulse' | 'wave'


export default /*@__PURE__*/_defineComponent({
  ...{ name: 'AcImage' },
  __name: 'index',
  props: {
    src: {},
    alt: { default: '' },
    mode: { default: 'img' },
    aspectRatio: {},
    width: {},
    height: {},
    objectFit: { default: 'cover' },
    loading: { default: 'lazy' },
    decoding: { default: 'auto' },
    radius: {},
    shimmerVariant: { default: 'sweep' },
    placeholder: { default: placeholderImage }
  },
  emits: ["load", "error"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const slots = useSlots()

const imgRef = ref<HTMLImageElement>()
const state = ref<ImageState>('loading')

// Заглушку показываем, когда src пуст или картинка не загрузилась — но только если
// задан placeholder и не передан кастомный слот error (он имеет приоритет на ошибке).
const isPlaceholder = computed(() => {
  if (!props.placeholder) return false
  if (!props.src) return true
  return state.value === 'error' && !slots.error
})
const resolvedSrc = computed(() => isPlaceholder.value ? props.placeholder : props.src)

const isLoading = computed(() => state.value === 'loading' && !isPlaceholder.value)
const isReady = computed(() => state.value === 'loaded' || isPlaceholder.value)

const toCssSize = (v: string | number | undefined): string | undefined => {
  if (v === undefined) return undefined
  return typeof v === 'number' ? `${v}px` : v
}

const reserveStyle = computed(() => ({
  aspectRatio: props.aspectRatio !== undefined ? String(props.aspectRatio) : undefined,
  width: toCssSize(props.width),
  height: toCssSize(props.height),
  borderRadius: toCssSize(props.radius)
}))

const fitStyle = computed(() => (isPlaceholder.value ? null : { objectFit: props.objectFit }))

const bgStyle = computed(() => ({
  backgroundImage: `url("${resolvedSrc.value}")`,
  backgroundSize: props.objectFit === 'contain' ? 'contain' : 'cover'
}))

let preloader: HTMLImageElement | null = null

function cancelPreloader (): void {
  if (preloader) {
    preloader.onload = null
    preloader.onerror = null
    preloader = null
  }
}

function preloadBackground (): void {
  cancelPreloader()
  preloader = new Image()
  preloader.onload = () => { state.value = 'loaded'; emit('load'); cancelPreloader() }
  preloader.onerror = () => { state.value = 'error'; emit('error'); cancelPreloader() }
  preloader.src = props.src
}

function onLoad (): void {
  state.value = 'loaded'
  emit('load')
}

function onError (): void {
  state.value = 'error'
  emit('error')
}

watch(() => props.src, () => {
  state.value = 'loading'
  if (props.mode === 'background') preloadBackground()
})

onMounted(() => {
  if (props.mode === 'background') {
    preloadBackground()
    return
  }
  if (imgRef.value?.complete && imgRef.value.naturalWidth > 0) {
    state.value = 'loaded'
  }
})

onBeforeUnmount(cancelPreloader)



return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "acImage",
    style: _normalizeStyle(reserveStyle.value)
  }, [
    _withDirectives(_createElementVNode("div", {
      class: _normalizeClass(["acImage_shimmer", `-${__props.shimmerVariant}`]),
      "data-test": "ac-image-shimmer"
    }, null, 2), [
      [_vShow, isLoading.value]
    ]),
    (__props.mode === 'img')
      ? (_openBlock(), _createElementBlock("img", {
          key: 0,
          ref_key: "imgRef",
          ref: imgRef,
          class: _normalizeClass(["acImage_img", { '-loaded': isReady.value }]),
          src: resolvedSrc.value,
          alt: __props.alt,
          loading: __props.loading,
          decoding: __props.decoding,
          style: _normalizeStyle(fitStyle.value),
          "data-test": "ac-image-img",
          onLoad: onLoad,
          onError: onError
        }, null, 46, _hoisted_1))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["acImage_bg", { '-loaded': isReady.value }]),
          style: _normalizeStyle(bgStyle.value),
          "data-test": "ac-image-bg"
        }, null, 6)),
    (state.value === 'error' && !isPlaceholder.value)
      ? _renderSlot(_ctx.$slots, "error", {}, undefined, undefined, 2)
      : _createCommentVNode("", true)
  ], 4))
}
}

})